// Client/src/pages/digi/Invoice/TemplateDesigner/geometry.js
//
// Pure geometry for dragging and resizing free elements, in millimetres inside
// their section. Snapping lines up an element's left/centre/right (and
// top/middle/bottom) with the section's edges and centre and with its
// siblings' edges and centres.

export const round1 = (n) => Math.round(Number(n) * 10) / 10;
export const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));
export const MIN_SIZE_MM = 2;

/** Snap targets for one section: x lines and y lines in mm. */
export function snapTargets(bandWidth, bandHeight, siblings) {
  const xs = [0, bandWidth / 2, bandWidth];
  const ys = [0, bandHeight / 2, bandHeight];
  for (const f of siblings || []) {
    xs.push(f.x, f.x + f.w / 2, f.x + f.w);
    ys.push(f.y, f.y + f.h / 2, f.y + f.h);
  }
  return { xs, ys };
}

// Best snap of any of `edges` (positions on the moving box) to any target.
function bestSnap(edges, targets, threshold) {
  let best = null;
  for (const e of edges) {
    for (const t of targets) {
      const d = t - e;
      if (Math.abs(d) <= threshold && (!best || Math.abs(d) < Math.abs(best.delta))) best = { delta: d, line: t };
    }
  }
  return best;
}

/**
 * Snap a moving frame. Returns the adjusted frame and the guide lines that
 * matched ({ x: mm|null, y: mm|null }).
 */
export function snapMove(frame, targets, threshold) {
  const sx = bestSnap([frame.x, frame.x + frame.w / 2, frame.x + frame.w], targets.xs, threshold);
  const sy = bestSnap([frame.y, frame.y + frame.h / 2, frame.y + frame.h], targets.ys, threshold);
  return {
    frame: { ...frame, x: round1(frame.x + (sx ? sx.delta : 0)), y: round1(frame.y + (sy ? sy.delta : 0)) },
    guides: { x: sx ? sx.line : null, y: sy ? sy.line : null },
  };
}

/**
 * Resize from a handle ("n", "se", ...) by a pointer delta in mm. `keepRatio`
 * (Shift) preserves the aspect ratio for corner handles. The moving edges snap.
 */
export function resizeFrame(start, handle, dx, dy, opts = {}) {
  let { x, y, w, h } = start;
  if (handle.includes("e")) w = start.w + dx;
  if (handle.includes("s")) h = start.h + dy;
  if (handle.includes("w")) {
    w = start.w - dx;
    x = start.x + dx;
  }
  if (handle.includes("n")) {
    h = start.h - dy;
    y = start.y + dy;
  }
  if (opts.keepRatio && handle.length === 2 && start.w > 0 && start.h > 0) {
    const ratio = start.w / start.h;
    if (Math.abs(w / start.w) > Math.abs(h / start.h)) h = w / ratio;
    else w = h * ratio;
    if (handle.includes("w")) x = start.x + start.w - w;
    if (handle.includes("n")) y = start.y + start.h - h;
  }
  if (w < MIN_SIZE_MM) {
    if (handle.includes("w")) x -= MIN_SIZE_MM - w;
    w = MIN_SIZE_MM;
  }
  if (h < MIN_SIZE_MM) {
    if (handle.includes("n")) y -= MIN_SIZE_MM - h;
    h = MIN_SIZE_MM;
  }
  const guides = { x: null, y: null };
  if (opts.targets && opts.threshold && !opts.keepRatio) {
    const t = opts.targets;
    if (handle.includes("e")) {
      const s = bestSnap([x + w], t.xs, opts.threshold);
      if (s) { w += s.delta; guides.x = s.line; }
    } else if (handle.includes("w")) {
      const s = bestSnap([x], t.xs, opts.threshold);
      if (s) { x += s.delta; w -= s.delta; guides.x = s.line; }
    }
    if (handle.includes("s")) {
      const s = bestSnap([y + h], t.ys, opts.threshold);
      if (s) { h += s.delta; guides.y = s.line; }
    } else if (handle.includes("n")) {
      const s = bestSnap([y], t.ys, opts.threshold);
      if (s) { y += s.delta; h -= s.delta; guides.y = s.line; }
    }
  }
  return { frame: { ...start, x: round1(x), y: round1(y), w: round1(Math.max(MIN_SIZE_MM, w)), h: round1(Math.max(MIN_SIZE_MM, h)) }, guides };
}

/** Insertion index among stacked boxes for a pointer y (container coordinates). */
export function insertionIndex(rects, y) {
  for (let i = 0; i < rects.length; i++) {
    if (y < rects[i].top + rects[i].height / 2) return i;
  }
  return rects.length;
}

export const HANDLES = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];
export const HANDLE_CURSORS = {
  nw: "nwse-resize", se: "nwse-resize", ne: "nesw-resize", sw: "nesw-resize",
  n: "ns-resize", s: "ns-resize", e: "ew-resize", w: "ew-resize",
};
